
interface PricingTier {
  id: 'starter' | 'growth' | 'enterprise';
  name: string;
  price: number;
  features: string[];
}

interface Step2PricingTierProps {
  selectedTier: string;
  onChange: (tier: string) => void;
}

const PRICING_TIERS: PricingTier[] = [
  {
    id: 'starter',
    name: 'Starter',
    price: 49,
    features: [
      '5,000 transactions/mo',
      '50,000 API calls/mo',
      '25 users',
      'Basic features',
      'Email support',
    ],
  },
  {
    id: 'growth',
    name: 'Growth',
    price: 149,
    features: [
      '10,000 transactions/mo',
      '100,000 API calls/mo',
      '50 users',
      'Advanced features',
      'Priority support',
    ],
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 499,
    features: [
      'Unlimited transactions',
      'Unlimited API calls',
      'Unlimited users',
      'All features',
      'Dedicated support',
    ],
  },
];

export function Step2PricingTier({ selectedTier, onChange }: Step2PricingTierProps) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Choose Your Pricing Tier</h2>
        <p className="mt-2 text-sm text-gray-600">
          Select the plan that fits your needs. You can upgrade or downgrade anytime.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
        {PRICING_TIERS.map((tier) => (
          <div
            key={tier.id}
            onClick={() => onChange(tier.id)}
            className={`relative cursor-pointer border-2 rounded-xl p-6 transition-all duration-200 ${
              selectedTier === tier.id
                ? 'border-blue-600 bg-blue-50 shadow-md scale-105'
                : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
            }`}
          >
            {/* Recommended badge for Growth */}
            {tier.id === 'growth' && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-600 text-white px-3 py-1 rounded-full text-xs font-semibold">
                Recommended
              </div>
            )}

            <div className="text-center">
              {/* Tier name */}
              <h3 className="text-sm font-bold text-gray-600 uppercase tracking-wide">
                {tier.name}
              </h3>

              {/* Price */}
              <div className="mt-4">
                <span className="text-4xl font-bold text-gray-900">
                  ${tier.price}
                </span>
                <span className="text-gray-600">/mo</span>
              </div>

              {/* Divider */}
              <div className="my-6 border-t border-gray-200" />

              {/* Features */}
              <ul className="space-y-3 text-left">
                {tier.features.map((feature, index) => (
                  <li
                    key={index}
                    className="flex items-start gap-2 text-sm text-gray-700"
                  >
                    <svg
                      className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {feature}
                  </li>
                ))}
              </ul>

              {/* Radio button */}
              <div className="mt-6 flex justify-center">
                <div
                  className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                    selectedTier === tier.id
                      ? 'border-blue-600 bg-blue-600'
                      : 'border-gray-300'
                  }`}
                >
                  {selectedTier === tier.id && (
                    <div className="w-3 h-3 rounded-full bg-white" />
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {!selectedTier && (
        <p className="text-center text-sm text-gray-600 italic">
          Please select a pricing tier to continue
        </p>
      )}
    </div>
  );
}
